/* eslint-disable object-curly-newline */
/* eslint-disable implicit-arrow-linebreak */
import React, { useState } from 'react';
import { navigate } from 'gatsby';

import loadable from '@loadable/component';
import classNames from 'classnames';
import differenceInDays from 'date-fns/differenceInDays';
import qs from 'query-string';

import LeadFormSwitch from './LeadFormSwitch';
import sendLeads from '../../util/formSubmissionHandler/sendLeads';
import sendRudderstackEvent from '../../util/rudderstack/sendRudderstackEvent';
import redirectToPlanListing from '../../util/formSubmissionHandler/planPageRedirect';
import {
  MULTI_STEP_SKELETON_FORMS,
  LEAD_FORM_LANG,
  INSURANCE_OPTIONS,
  LEADFORM_EXCLUDE_STATUS
} from '../../data/constant';
import { sendToHubspot } from '../../util/formSubmissionHandler/sendToHubspot';
import httpRequest from '../../util/httpRequest';
import utm from '../../util/utm';

interface LeadformInterface {
  showTypeDropdown: boolean;
  showRibbon: boolean;
  leadFormTitle: string;
  leadFormDescription?: string;
  leadFormCTA: string;
  discountCode?: string;
  leadFormType: string;
  autoBrandList: any[];
  fomo: object;
  popularCarBrands?: any[];
  language?: 'en' | 'id';
  isLoading?: boolean;
}

const LeadFormSkeleton = (props: LeadformInterface) => {
  const {
    showTypeDropdown,
    leadFormType,
    showRibbon,
    leadFormTitle,
    leadFormDescription,
    leadFormCTA,
    autoBrandList,
    popularCarBrands,
    language,
    fomo,
    discountCode
  } = props;

  const [formType, setFormType] = useState(leadFormType);
  const [showCallNotif, setShowCallNotif] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInvalidPhone, setShowInvalidPhone] = useState(false);

  const isMultiStepSkeleton = MULTI_STEP_SKELETON_FORMS.includes(formType);

  const cleanPhoneNumber = (phone, withPrefix) => {
    const whiteSpace = /\s/g;
    let noLeadingNumber = /^(0|62)/g;
    let replacement:any = '';

    if (withPrefix) {
      noLeadingNumber = /^(62)/g;
      replacement = 0;
    }

    return phone.replace(whiteSpace, '').replace(noLeadingNumber, replacement);
  };

  const LeadFormTypeDropdown = () => (
    <div className="w-11/12 lg:w-4/12 m-auto flex flex-row items-center bg-blue-900 rounded">
      <span className="w-4/12 text-white font-bold p-2">Jenis Asuransi : </span>
      <select
        name="insuranceType"
        id="insuranceType"
        className="w-8/12 bg-blue-900 text-white"
        onChange={(e) => {
          setFormType(e.target.value);
        }}
        value="Asuransi"
      >
        {/* TODO: Check dropdown list of brand page */}
        {INSURANCE_OPTIONS.map((insurance) => (
          <option key={insurance.label} value={insurance.value}>
            {insurance.label}
          </option>
        ))}
      </select>
    </div>
  );

  const shouldShowTypeDropdown = () => {
    if (showTypeDropdown) return <LeadFormTypeDropdown />;
    const LeadFormHeader = loadable(() => import('./LeadFormHeader'));
    return (
      <LeadFormHeader
        title={leadFormTitle}
        description={leadFormDescription}
        fomo={fomo}
        showRibbon={showRibbon}
        discountCode={discountCode}
      />
    );
  };

  const travelLeadFormHandler = (data) => {
    const departDate = `${data.departureYear}-${data.departureMonth}-${data.departureDate}`;
    const returnDate = `${data.returnYear}-${data.returnMonth}-${data.returnDate}`;
    const dataConfig = {
      category: 'travel',
      peopleAmount: data.peopleAmount,
      destination: data.destinationCode,
      departureDate: departDate,
      returnDate,
      range: JSON.stringify({
        from: {
          day: Number(data.departureDate),
          month: Number(data.departureMonth),
          year: Number(data.departureYear)
        },
        to: {
          day: Number(data.returnDate),
          month: Number(data.returnMonth),
          year: Number(data.returnYear)
        }
      }),
      travel_protection_type: data.travelProtectionType,
      travel_type: data.travelType,
      duration:
        departDate === returnDate
          ? 1
          : differenceInDays(new Date(returnDate), new Date(departDate))
    };
    const query = {
      ...dataConfig,
      gender: 'MALE',
      age: '18',
      budget: '500000',
      isSalary: 'false',
      spouse: JSON.stringify({}),
      childs: JSON.stringify([]),
      type: 'individual',
      selfChecked: true,
      sort_by: 'base_premium.asc'
    };
    navigate(`https://lifepal.tech/plans?${qs.stringify(query)}`);
  };

  const businessLeadFormHandler = async (data, leadFormVariation) => {
    const resHubSpot = await sendToHubspot(data);
    if (resHubSpot.status && resHubSpot.status === 'error') {
      setIsLoading(false);
      return;
    }
    if (leadFormVariation === 'businessLeadFormV2') {
      navigate('https://app.lifepal.co.id/register/');
    } else {
      navigate(
        `https://app.lifepal.co.id/register?${qs.stringify({
          number_of_employee: data.employees
        })}`
      );
    }
  };

  const leadFormVariationHandlers = (data, leadFormVariation) => {
    const handlers = {
      moto: () => redirectToPlanListing(data),
      businessLeadForm: () => businessLeadFormHandler(data, leadFormVariation),
      businessLeadFormV2: () => businessLeadFormHandler(data, leadFormVariation),
      travelFormV2: () => travelLeadFormHandler(data)
    };
    return handlers[leadFormVariation]();
  };

  const sendToDgraph = async (data) => {
    const utmLeads = utm.getAll() || {};
    const trackingData = {
      category: data.category,
      leadInfo: {
        name: data.fullName || '',
        phoneNumber: data.phoneNumber,
        email: data.email || '',
        salary: 0,
        members: null,
        vehicleSpecification: {
          brand: data.brand,
          model: data.model,
          vehicleType: data.type,
          year: Number(data.year),
          area: data.area,
          region: data.areaCode
        },
        hasCompletedPSF: true,
        hasCreditCard: false
      },
      utmSource: utmLeads.utm_source || '',
      utmMedium: utmLeads.utm_medium || '',
      utmCampaign: utmLeads.utm_campaign || ''
    };
    let res;
    try {
      res = await httpRequest(`${process.env.GATSBY_CORE_URL}asuransi/api/core/track/`, {
        method: 'POST',
        body: JSON.stringify(trackingData)
      });
      return res;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  const CallNotification = loadable(() => import('../Widgets/CallNotification'));
  const InvalidPhonePopup = loadable(() => import('../Widgets/InvalidPhonePopup'));

  const handleLeadSubmit = async (data: any, leadCategory) => {
    setIsLoading(true);
    data.phoneNumber = cleanPhoneNumber(data.phoneNumber, true);
    if (leadCategory === 'auto' || leadCategory === 'health') {
      setShowCallNotif(true);
      const lead = await sendLeads(data);
      if (lead && lead.id) {
        await sendToDgraph(data);
        const isPhoneValid = lead.is_number_valid;

        if (formType === 'auto_v2') {
          if (!isPhoneValid) {
            setShowCallNotif(false);
            setShowInvalidPhone(true);
            return '';
          }
        }

        sendRudderstackEvent('lead-submit', {
          leadID: lead.id,
          ...data
        });
        if (!LEADFORM_EXCLUDE_STATUS.includes(lead.status)) {
          sendRudderstackEvent(
            'lead-submit',
            {
              eventLabel: data.category,
              eventAction: 'lead-submit',
              eventCategory: 'lead-submit'
            },
            {
              all: false,
              'Google Analytics': true
            }
          );
        }

        setShowCallNotif(false);
        setIsLoading(false);
        if (data.category === 'auto') {
          const areaCode = data.areaCode.split(' ');
          data.namePlan = `${data.brand}_${data.model}_${data.type}_${areaCode[0]}`;
          redirectToPlanListing(data);
        }
        redirectToPlanListing(data);
      } else {
        setShowCallNotif(false);
        setIsLoading(false);
        alert('Terjadi kesalahan pada sistem, silahkan coba beberapa saat lagi');
      }
    }
    leadFormVariationHandlers(data, leadCategory);
  };

  return (
    <div
      className={classNames(
        'relative bg-brand-primary-color',
        isMultiStepSkeleton ? 'py-4 px-8 lg:px-16' : 'bg-brand-primary-color px-4 pb-4'
      )}
    >
      {!isMultiStepSkeleton && shouldShowTypeDropdown()}
      <LeadFormSwitch
        type={formType}
        isPopup={false}
        isSidebar={false}
        autoBrandList={autoBrandList}
        popularCarBrands={popularCarBrands}
        language={language}
        isLoading={isLoading}
        leadFormCTA={leadFormCTA}
        handleLeadSubmit={handleLeadSubmit}
      />
      {!isMultiStepSkeleton && (
        <div className="lead__form-toc">
          <div className="col">
            {LEAD_FORM_LANG[language].term}
            <a
              href="https://lifepal.co.id/ketentuan"
              target="_blank"
              rel="noreferrer"
              title="Syarat dan Ketentuan"
            >
              {LEAD_FORM_LANG[language].termLink}
            </a>
            <span />
            {language === 'id' && LEAD_FORM_LANG.id.termExt}
          </div>
        </div>
      )}
      {showCallNotif && <CallNotification />}
      {showInvalidPhone && <InvalidPhonePopup onClick={() => setShowInvalidPhone(false)} />}
    </div>
  );
};

export default LeadFormSkeleton;
